import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ReactComponent as ArrowLeftIcon } from '../../assets/images/keyboard_arrow_left.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/images/keyboard_arrow_right.svg';
import './PeriodSelector.scss';
import Calendar from '../input/Calendar';
import moment from 'moment';

/** value.from/value.to mogą być Date lub moment – normalizacja do Date */
const toDate = (v) => (v && (v.toDate ? v.toDate() : new Date(v)));

/** Mapowanie periodType z value na klucz filterType */
const periodTypeToFilterType = (value, defaultFilterType) => {
    const pt = value?.periodType;
    if (!pt) return defaultFilterType;
    if (pt === 'day') return 'today';
    if (pt === 'week') return 'currentWeek';
    if (pt === 'month') {
        const from = value?.from ? moment(toDate(value.from)) : null;
        const now = moment();
        return from && from.month() === now.month() && from.year() === now.year() ? 'currentMonth' : 'previousMonth';
    }
    if (pt === 'days90') return 'last90Days';
    if (pt === 'year') return 'last12Months';
    if (pt === 'custom') return 'custom';
    return defaultFilterType;
};

const PeriodSelector = ({ value, onChange, className = '', defaultFilterType = 'currentMonth' }) => {
    const { t } = useTranslation();
    const overlayRef = useRef(null);
    const skipNextOnChangeRef = useRef(false);
    const isControlledRef = useRef(value?.from != null && value?.to != null);
    /** true gdy filterType został ustawiony z value (sync z URL) – wtedy nie wywoływać applyInitialPeriod */
    const filterTypeSyncedFromValueRef = useRef(false);

    const [filterType, setFilterType] = useState(() =>
        value?.from != null && value?.to != null ? periodTypeToFilterType(value, defaultFilterType) : defaultFilterType
    );
    const [period, setPeriod] = useState(() =>
        value?.from != null && value?.to != null ? { from: toDate(value.from), to: toDate(value.to) } : null
    );
    const [label, setLabel] = useState(null);

    const [customPeriod, setCustomPeriod] = useState(null);
    const periodOptions = [
        { key: 'today', label: t('periodSelector.today'), period: 'day' },
        { key: 'currentWeek', label: t('periodSelector.currentWeek'), period: 'week' },
        { key: 'currentMonth', label: t('periodSelector.currentMonth'), period: 'month' },
        { key: 'previousMonth', label: t('periodSelector.previousMonth'), period: 'month' },
        { key: 'last90Days', label: t('periodSelector.last90Days'), period: 'days90' },
        { key: 'last12Months', label: t('periodSelector.last12Months'), period: 'year' },
    ];

    useEffect(() => {
        const handleScroll = () => {
            if (overlayRef.current) {
                overlayRef.current.align(); // przelicza pozycję panelu
                //overlayRef.current.hide();
            }
        };    
        window.addEventListener('scroll', handleScroll, true);    
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    // Formatowanie daty do wyświetlania
    const formatDate = (date) => {
        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getPeriodType = () => {
        if (filterType === 'custom') {
            return 'custom';
        }
        return periodOptions.find(option => option.key === filterType)?.period;
    }

    // Funkcja do przesuwania okresu
    const changePeriod = (direction) => {
        const periodType = getPeriodType();
        if (!periodType) return;

        if (periodType === 'day') {            
            const from = moment(period?.from).add(direction, 'days').startOf('day');
            const to = from.clone().add(1, 'day');
            setPeriod({ from: from.toDate(), to: to.toDate() });
        } else if (periodType === 'week') {
            const newStart = moment(period?.from).add(direction * 7, 'days');
            setPeriod({
                from: newStart.clone().startOf('week').toDate(), // poniedziałek
                to: newStart.clone().endOf('week').toDate()      // niedziela
              });
        } else if (periodType === 'month') {
            const from = moment(period?.from).add(direction, 'months').clone().startOf('month');
            const to = from.clone().endOf('month');
            setPeriod({ from : from.toDate(), to : to.toDate()});
        } else if (periodType === 'days90') {
            const from = moment(period?.from).add(direction * 90, 'days').startOf('day');
            const to = from.clone().add(90, 'days').endOf('day');
            setPeriod({ from: from.toDate(), to: to.toDate() });
        } else if (periodType === 'year') {
            // Przesuń o rok
            const from = moment(period?.from).add(direction, 'years').startOf('day');
            const to = from.clone().add(1, 'years').endOf('day');
            setPeriod({ from: from.toDate(), to: to.toDate() });
        } else if (periodType === 'custom') {
            const currentDaysDifference = moment(period?.to).startOf('day').diff(moment(period?.from).startOf('day'), 'days');
            const from = moment(period?.from).add(direction * (currentDaysDifference + 1), 'days').startOf('day');
            const to = from.clone().add(currentDaysDifference, 'days').endOf('day');            
            setPeriod({ from: from.toDate(), to: to.toDate() });
        }
    };


    const generatePeriodLabel = () => {

        const periodType = getPeriodType();
        const now = new Date();
        switch (periodType) {
            case 'day':
                const isToday = moment(period?.from).isSame(now, 'day');
                if (isToday) {
                    const hoursPassed = moment().diff(moment(period?.from).startOf('day'), 'hours');
                    const minutesPassed = moment().diff(moment(period?.from).startOf('day'), 'minutes') % 60;
                    return `${t('periodSelector.today')} (${hoursPassed}h ${minutesPassed}m )`;
                } else {
                    return t('periodSelector.day');
                }
            case 'week':
                const currentWeekStart = moment().startOf('week');
                const isCurrentWeek = moment(period?.from).isSame(currentWeekStart, 'week');
                if (isCurrentWeek) {
                    const daysPassedWeek = moment().startOf('day').diff(currentWeekStart, 'days');
                    const hoursInCurrentDay = moment().diff(currentWeekStart.clone().add(daysPassedWeek, 'days'), 'hours');
                    const weekLabel = `${daysPassedWeek}d ${hoursInCurrentDay}h`;
                    return `${t('periodSelector.currentWeek')} (${weekLabel})`
                } else {
                    return t('periodSelector.week');
                }
            case 'month':
                const currentMonthStart = moment().startOf('month');
                const isCurrentMonth = moment(period?.from).isSame(currentMonthStart, 'month');
                if (isCurrentMonth) {
                    const daysPassedMonth = moment().startOf('day').diff(currentMonthStart, 'days');
                    const hoursInCurrentDay = moment().diff(currentMonthStart.clone().add(daysPassedMonth, 'days'), 'hours');
                    const monthLabel = `${daysPassedMonth}d ${hoursInCurrentDay}h`;
                    return `${t('periodSelector.currentMonth')} (${monthLabel})`;
                } else {
                    // Wyświetl nazwę miesiąca i rok
                    const monthIndex = period?.from?.getMonth();
                    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
                    const monthName = t(`financialReportWidget.months.${monthKeys[monthIndex]}`);
                    const year = period?.from?.getFullYear();
                    return `${monthName} ${year}`;
                }
            case 'days90':
                // Sprawdź czy to bieżący 90 dni
                const current90DaysStart = moment().startOf('day');
                const isCurrent90Days = moment(period?.from).isSame(current90DaysStart, 'day');
                if (isCurrent90Days) {
                    return t('periodSelector.last90Days');
                } else {
                    return t('periodSelector.90Days');
                }
            case 'year':
                // Sprawdź czy to bieżący rok
                const currentYear = now.getFullYear();
                const isCurrentYear = period?.to?.getFullYear() === currentYear;
                if (isCurrentYear) {
                    return t('periodSelector.last12Months');
                } else {
                    // Wyświetl liczbę dni między from i to
                    const daysDifference = moment(period?.to).startOf('day').diff(moment(period?.from).startOf('day'), 'days');
                    return t('periodSelector.days', { count: daysDifference });
                }
            case 'custom':
                const daysDifference = moment(period?.to).startOf('day').diff(moment(period?.from).startOf('day'), 'days');
                return t('periodSelector.days', { count: daysDifference + 1 });
        }
    }

    useEffect(() => {
        const newLabel = generatePeriodLabel();
        setLabel(newLabel);
        if (skipNextOnChangeRef.current) {
            skipNextOnChangeRef.current = false;
        } else if (period?.from && period?.to) {
            onChange({ from: period.from, to: period.to, periodType: getPeriodType() });
        }
        overlayRef.current?.hide();
    }, [period, t]);

    // Synchronizacja z zewnętrznym value (controlled, np. z URL). Nie nadpisuj, gdy value = aktualny period (np. parent się przeładował z tą samą wartością) – inaczej zgubimy wybór użytkownika (Miesiąc / Ostatni rok).
    useEffect(() => {
        if (value?.from == null || value?.to == null) return;
        const vFrom = toDate(value.from).getTime();
        const vTo = toDate(value.to).getTime();
        if (period?.from != null && period?.to != null && new Date(period.from).getTime() === vFrom && new Date(period.to).getTime() === vTo) {
            return;
        }
        skipNextOnChangeRef.current = true;
        filterTypeSyncedFromValueRef.current = true;
        setPeriod({ from: toDate(value.from), to: toDate(value.to) });
        setFilterType(periodTypeToFilterType(value, defaultFilterType));
    }, [value]);

    // Funkcja do ustawiania okresu
    const applyInitialPeriod = () => {
        const now = moment();
        let from, to;

        switch (filterType) {
            case 'today':
                from = now.clone().startOf('day');
                to = from.clone().add(1, 'day');
                break;
            case 'currentWeek':
                from = now.clone().startOf('week');
                to = from.clone().endOf('week');
                break;
            case 'currentMonth':
                from = now.clone().startOf('month');
                to = now.clone(); //from.clone().endOf('month');
                break;
            case 'previousMonth':
                from = now.clone().subtract(1, 'month').startOf('month');
                to = from.clone().endOf('month');
                break;
            case 'last90Days':
                from = now.clone().subtract(90, 'days').startOf('day');
                to = now.clone(); //now;
                break;
            case 'last12Months':
                from = now.clone().subtract(1, 'year').startOf('day');
                to = now.clone(); //now;
                break;
            case 'custom':
                if (customPeriod) {
                    from = moment(customPeriod[0]).startOf('day');
                    to = moment(customPeriod[1]).endOf('day');
                }
                break;
            default:
                return;
        }

        if (from == null || to == null) return;
        setPeriod({ from: from.toDate(), to: to.toDate() });
        if (filterType !== 'custom') {
            setCustomPeriod(null);
        }
    };

    useEffect(() => {
        if (!filterType) return;
        if (filterTypeSyncedFromValueRef.current) {
            filterTypeSyncedFromValueRef.current = false;
            isControlledRef.current = false;
            overlayRef.current?.hide();
            return;
        }
        if (isControlledRef.current && period?.from != null && period?.to != null) {
            isControlledRef.current = false;
            overlayRef.current?.hide();
            return;
        }
        applyInitialPeriod();
        overlayRef.current?.hide();
    }, [filterType]);

    const getStartOfDay = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    }
    const getEndOfDay = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
    }

    useEffect(() => {
        if (customPeriod && customPeriod.length === 2 && customPeriod[0] && customPeriod[1]) {
            setPeriod({ from: getStartOfDay(customPeriod[0]), to: getEndOfDay(customPeriod[1]) });
            setFilterType('custom');
        } else {
            //setFilterType(defaultFilterType);
        }
    }, [customPeriod]);


    return (
        <div className={`period-selector ${className}`}>
            {/* Główny komponent z datami i strzałkami */}
            <div className="period-display" onClick={(e) => {
                overlayRef.current?.toggle(e);
            }}>
                <ArrowLeftIcon
                    className="period-arrow"
                    onClick={(e) => {
                        e.stopPropagation();
                        changePeriod(-1);
                    }}
                />


                <div className="period-info">
                    <div className="period-label">
                        {label}
                    </div>
                    {period?.from && period?.to && (
                        <div className="period-dates">
                            {filterType === 'today'
                                ? formatDate(new Date(period.from))
                                : `${formatDate(new Date(period.from))} - ${formatDate(new Date(period.to))}`
                            }
                        </div>
                    )}
                </div>

                <ArrowRightIcon
                    className="period-arrow"
                    onClick={(e) => {
                        e.stopPropagation();
                        changePeriod(1);
                    }}
                />
            </div>

            {/* OverlayPanel z opcjami */}
            <OverlayPanel
                ref={overlayRef}
                className="period-selector-overlay"
                showCloseIcon={false}
                dismissable={true}
                baseZIndex={1000}
                appendTo={document.body}
                unstyled={true}
            //style={{backgroundColor: 'yellow', width: '300px'}}
            >
                <div className="period-selector-content">
                    <div className="period-options">
                        {periodOptions.map(option => (
                            <div key={option.key}
                                className='period-option-wrapper'
                                onClick={() => {
                                    setFilterType(option.key);
                                }}
                            >
                                <div className={`period-option ${option.key === filterType ? 'active' : ''}`}>
                                    {option.label}
                                </div>

                            </div>
                        ))}
                    </div>

                    <div className="separator" />
                    <div className="custom-range">
                        <Calendar label={t('periodSelector.custom')} inputClassName='small' value={customPeriod}
                            onChange={(e) => {
                                setCustomPeriod(e.value);
                            }}
                            selectionMode="range" readOnlyInput hideOnRangeSelection
                        />

                    </div>
                </div>
            </OverlayPanel>
        </div>
    );
};

export default PeriodSelector;
